import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Grid,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import StructuredModal from 'src/views/components/structuredModal/structuredModal';
import ControlledTextField from 'src/views/components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from 'src/views/components/controlledComponents/ControlledSelectField.component';
import ControlledDateField from 'src/views/components/controlledComponents/ControlledDateField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useHealthAndSafetyState,
  useHealthAndSafetyActions,
  EViewState,
} from '../../../../contexts/healthAndSafety.context';
import { useHealthAndSafety } from '../../../../hooks/useHealthAndSafety';
import { THealthIncident } from 'src/apis/administration/administration.interfaces';
import { TKeyValue } from 'src/utils/commonInterfaces';
import dayjs, { Dayjs } from 'dayjs';
import {
  isPickerDateRangeOrdered,
  zOptionalNullablePickerDate,
  zRequiredPickerDate,
} from 'src/utils/zodFormHelpers';

type HealthIncidentFormData = {
  incidentType:
  | 'INJURY'
  | 'ILLNESS'
  | 'ACCIDENT'
  | 'EMERGENCY'
  | 'ALLERGY'
  | 'OTHER';
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  reportedBy: string;
  reportedDate: Dayjs | null;
  incidentDate: Dayjs | null;
  location: string;
  description: string;
  affectedPersonType: 'STUDENT' | 'STAFF' | 'VISITOR';
  affectedPersonId: string;
  symptoms?: string;
  firstAidProvided: boolean;
  medicalAttentionRequired: boolean;
  hospitalVisited?: string;
  followUpRequired: boolean;
  followUpDate?: Dayjs | null;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  resolution?: string;
  preventiveMeasures?: string;
};

const getHealthIncidentFormSchema = (translateText: (key: string) => string) =>
  z
    .object({
    incidentType: z.enum(
      ['INJURY', 'ILLNESS', 'ACCIDENT', 'EMERGENCY', 'ALLERGY', 'OTHER'],
      {
        required_error: translateText('common.fieldRequired'),
      }
    ),
    severity: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'], {
      required_error: translateText('common.fieldRequired'),
    }),
    reportedBy: z.string().min(1, translateText('common.fieldRequired')),
    reportedDate: zRequiredPickerDate({
      requiredMessage: translateText('common.fieldRequired'),
      invalidMessage: translateText('common.dateInvalid'),
    }),
    incidentDate: zRequiredPickerDate({
      requiredMessage: translateText('common.fieldRequired'),
      invalidMessage: translateText('common.dateInvalid'),
    }),
    location: z.string().min(1, translateText('common.fieldRequired')),
    description: z.string().min(1, translateText('common.fieldRequired')),
    affectedPersonType: z.enum(['STUDENT', 'STAFF', 'VISITOR'], {
      required_error: translateText('common.fieldRequired'),
    }),
    affectedPersonId: z.string().min(1, translateText('common.fieldRequired')),
    symptoms: z.string().optional(),
    firstAidProvided: z.boolean(),
    medicalAttentionRequired: z.boolean(),
    hospitalVisited: z.string().optional(),
    followUpRequired: z.boolean(),
    followUpDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    status: z.enum(['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'], {
      required_error: translateText('common.fieldRequired'),
    }),
    resolution: z.string().optional(),
    preventiveMeasures: z.string().optional(),
  })
    .refine(
      (data) =>
        isPickerDateRangeOrdered(data.incidentDate, data.reportedDate),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['reportedDate'],
      }
    )
    .refine(
      (data) =>
        !data.followUpDate ||
        isPickerDateRangeOrdered(data.incidentDate, data.followUpDate),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['followUpDate'],
      }
    );

const AddEditHealthIncidentsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedIncident, editMode, viewState, currentViewType } =
    useHealthAndSafetyState();
  const { setViewState, setEditMode, selectIncident } =
    useHealthAndSafetyActions();
  const { createIncident, updateIncident, isLoading, errorMessage } =
    useHealthAndSafety();

  const isEditMode = editMode === 'edit' && selectedIncident;
  const isOpen =
    (viewState === EViewState.ADD || viewState === EViewState.EDIT) &&
    currentViewType === 'incidents';

  const incidentFormSchema = getHealthIncidentFormSchema(translateText);

  const defaultValues: HealthIncidentFormData = {
    incidentType: 'INJURY',
    severity: 'LOW',
    reportedBy: '',
    reportedDate: dayjs(),
    incidentDate: dayjs(),
    location: '',
    description: '',
    affectedPersonType: 'STUDENT',
    affectedPersonId: '',
    symptoms: '',
    firstAidProvided: false,
    medicalAttentionRequired: false,
    hospitalVisited: '',
    followUpRequired: false,
    followUpDate: null,
    status: 'OPEN',
    resolution: '',
    preventiveMeasures: '',
  };

  const methods = useForm<HealthIncidentFormData>({
    resolver: zodResolver(incidentFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (isEditMode && selectedIncident) {
      reset({
        incidentType: selectedIncident.incidentType,
        severity: selectedIncident.severity,
        reportedBy: selectedIncident.reportedBy,
        reportedDate: selectedIncident.reportedDate
          ? dayjs(selectedIncident.reportedDate)
          : dayjs(),
        incidentDate: selectedIncident.incidentDate
          ? dayjs(selectedIncident.incidentDate)
          : dayjs(),
        location: selectedIncident.location,
        description: selectedIncident.description,
        affectedPersonType: selectedIncident.affectedPersonType,
        affectedPersonId: selectedIncident.affectedPersonId,
        symptoms: selectedIncident.symptoms || '',
        firstAidProvided: selectedIncident.firstAidProvided,
        medicalAttentionRequired: selectedIncident.medicalAttentionRequired,
        hospitalVisited: selectedIncident.hospitalVisited || '',
        followUpRequired: selectedIncident.followUpRequired,
        followUpDate: selectedIncident.followUpDate
          ? dayjs(selectedIncident.followUpDate)
          : null,
        status: selectedIncident.status,
        resolution: selectedIncident.resolution || '',
        preventiveMeasures: selectedIncident.preventiveMeasures || '',
      });
    } else if (isOpen && !isEditMode) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, selectedIncident, isOpen, reset]);

  const handleClose = () => {
    if (isEditMode) {
      // Return to details view when canceling from edit mode
      setViewState(EViewState.DETAILS);
    } else {
      // Return to list view when canceling from add mode
      setViewState(EViewState.LIST);
      selectIncident(undefined);
    }
    setEditMode(undefined);
    reset(defaultValues);
  };

  const onSubmitForm = async (data: HealthIncidentFormData) => {
    try {
      const incidentData: Omit<
        THealthIncident,
        'id' | 'createdAt' | 'updatedAt'
      > = {
        incidentType: data.incidentType,
        severity: data.severity,
        reportedBy: data.reportedBy,
        reportedDate: data.reportedDate
          ? data.reportedDate.toDate()
          : new Date(),
        incidentDate: data.incidentDate
          ? data.incidentDate.toDate()
          : new Date(),
        location: data.location,
        description: data.description,
        affectedPersonType: data.affectedPersonType,
        affectedPersonId: data.affectedPersonId,
        symptoms: data.symptoms,
        firstAidProvided: data.firstAidProvided,
        medicalAttentionRequired: data.medicalAttentionRequired,
        hospitalVisited: data.hospitalVisited,
        followUpRequired: data.followUpRequired,
        followUpDate: data.followUpDate
          ? data.followUpDate.toDate()
          : undefined,
        status: data.status,
        resolution: data.resolution,
        preventiveMeasures: data.preventiveMeasures,
      };

      if (isEditMode && selectedIncident) {
        await updateIncident(selectedIncident.id, incidentData);
      } else {
        await createIncident(incidentData);
      }

      handleClose();
    } catch (error) {
      console.error('Error saving health incident:', error);
    }
  };

  const incidentTypeOptions: TKeyValue<string>[] = [
    {
      key: 'INJURY',
      value: translateText('administration.healthAndSafety.values.injury'),
    },
    {
      key: 'ILLNESS',
      value: translateText('administration.healthAndSafety.values.illness'),
    },
    {
      key: 'ACCIDENT',
      value: translateText('administration.healthAndSafety.values.accident'),
    },
    {
      key: 'EMERGENCY',
      value: translateText('administration.healthAndSafety.values.emergency'),
    },
    {
      key: 'ALLERGY',
      value: translateText('administration.healthAndSafety.values.allergy'),
    },
    {
      key: 'OTHER',
      value: translateText('administration.healthAndSafety.values.other'),
    },
  ];

  const severityOptions: TKeyValue<string>[] = [
    {
      key: 'LOW',
      value: translateText('administration.healthAndSafety.values.low'),
    },
    {
      key: 'MEDIUM',
      value: translateText('administration.healthAndSafety.values.medium'),
    },
    {
      key: 'HIGH',
      value: translateText('administration.healthAndSafety.values.high'),
    },
    {
      key: 'CRITICAL',
      value: translateText('administration.healthAndSafety.values.critical'),
    },
  ];

  const affectedPersonTypeOptions: TKeyValue<string>[] = [
    {
      key: 'STUDENT',
      value: translateText('administration.healthAndSafety.values.student'),
    },
    {
      key: 'STAFF',
      value: translateText('administration.healthAndSafety.values.staff'),
    },
    {
      key: 'VISITOR',
      value: translateText('administration.healthAndSafety.values.visitor'),
    },
  ];

  const statusOptions: TKeyValue<string>[] = [
    {
      key: 'OPEN',
      value: translateText('administration.healthAndSafety.values.open'),
    },
    {
      key: 'IN_PROGRESS',
      value: translateText('administration.healthAndSafety.values.inProgress'),
    },
    {
      key: 'RESOLVED',
      value: translateText('administration.healthAndSafety.values.resolved'),
    },
    {
      key: 'CLOSED',
      value: translateText('administration.healthAndSafety.values.closed'),
    },
  ];

  return (
    <div data-testid="add-edit-health-incidents-container">
      <StructuredModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          isEditMode
            ? translateText(
              'administration.healthAndSafety.editHealthIncidents'
            ) || 'Edit Health Incident'
            : translateText(
              'administration.healthAndSafety.addHealthIncidents'
            ) || 'Add Health Incident'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={isLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          errorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="health-incident-error-message"
            >
              {errorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="health-incident-form"
          >
            <Grid container spacing={3} padding={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {translateText(
                    'administration.healthAndSafety.labels.basicInformation'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="incidentType"
                  label={translateText(
                    'administration.healthAndSafety.columns.type'
                  )}
                  name="incidentType"
                  control={control}
                  itemList={incidentTypeOptions}
                  errorMessage={errors.incidentType?.message}
                  isRequired
                  data-testid="incident-type-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="severity"
                  label={translateText(
                    'administration.healthAndSafety.columns.severity'
                  )}
                  name="severity"
                  control={control}
                  itemList={severityOptions}
                  errorMessage={errors.severity?.message}
                  isRequired
                  data-testid="severity-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledDateField
                  id="incidentDate"
                  label={translateText(
                    'administration.healthAndSafety.columns.date'
                  )}
                  name="incidentDate"
                  control={control}
                  errorMessage={errors.incidentDate?.message}
                  required
                  data-testid="incident-date-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledDateField
                  id="reportedDate"
                  label={translateText(
                    'administration.healthAndSafety.labels.reportedDate'
                  )}
                  name="reportedDate"
                  control={control}
                  errorMessage={errors.reportedDate?.message}
                  required
                  data-testid="reported-date-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="location"
                  label={translateText(
                    'administration.healthAndSafety.columns.location'
                  )}
                  name="location"
                  control={control}
                  errorMessage={errors.location?.message}
                  required
                  data-testid="location-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="reportedBy"
                  label={translateText(
                    'administration.healthAndSafety.columns.reportedBy'
                  )}
                  name="reportedBy"
                  control={control}
                  errorMessage={errors.reportedBy?.message}
                  required
                  data-testid="reported-by-field"
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="description"
                  label={translateText(
                    'administration.healthAndSafety.columns.description'
                  )}
                  name="description"
                  control={control}
                  errorMessage={errors.description?.message}
                  multiline
                  rows={3}
                  required
                  data-testid="description-field"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.healthAndSafety.labels.affectedPerson'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="affectedPersonType"
                  label={translateText(
                    'administration.healthAndSafety.columns.personType'
                  )}
                  name="affectedPersonType"
                  control={control}
                  itemList={affectedPersonTypeOptions}
                  errorMessage={errors.affectedPersonType?.message}
                  isRequired
                  data-testid="affected-person-type-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="affectedPersonId"
                  label={translateText(
                    'administration.healthAndSafety.columns.personId'
                  )}
                  name="affectedPersonId"
                  control={control}
                  errorMessage={errors.affectedPersonId?.message}
                  required
                  data-testid="affected-person-id-field"
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="symptoms"
                  label={translateText(
                    'administration.healthAndSafety.labels.symptoms'
                  )}
                  name="symptoms"
                  control={control}
                  errorMessage={errors.symptoms?.message}
                  multiline
                  rows={2}
                  data-testid="symptoms-field"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.healthAndSafety.labels.medicalInformation'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={methods.watch('firstAidProvided')}
                      onChange={(e) =>
                        methods.setValue('firstAidProvided', e.target.checked)
                      }
                      data-testid="first-aid-provided-switch"
                    />
                  }
                  label={translateText(
                    'administration.healthAndSafety.columns.firstAid'
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={methods.watch('medicalAttentionRequired')}
                      onChange={(e) =>
                        methods.setValue(
                          'medicalAttentionRequired',
                          e.target.checked
                        )
                      }
                      data-testid="medical-attention-required-switch"
                    />
                  }
                  label={translateText(
                    'administration.healthAndSafety.columns.medicalCare'
                  )}
                />
              </Grid>

              {methods.watch('medicalAttentionRequired') && (
                <Grid item xs={12} md={6}>
                  <ControlledTextField
                    id="hospitalVisited"
                    label={translateText(
                      'administration.healthAndSafety.labels.hospitalVisited'
                    )}
                    name="hospitalVisited"
                    control={control}
                    errorMessage={errors.hospitalVisited?.message}
                    data-testid="hospital-visited-field"
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={methods.watch('followUpRequired')}
                      onChange={(e) =>
                        methods.setValue('followUpRequired', e.target.checked)
                      }
                      data-testid="follow-up-required-switch"
                    />
                  }
                  label={translateText(
                    'administration.healthAndSafety.labels.followUpRequired'
                  )}
                />
              </Grid>

              {methods.watch('followUpRequired') && (
                <Grid item xs={12} md={6}>
                  <ControlledDateField
                    id="followUpDate"
                    label={translateText(
                      'administration.healthAndSafety.labels.followUpDate'
                    )}
                    name="followUpDate"
                    control={control}
                    errorMessage={errors.followUpDate?.message}
                    data-testid="follow-up-date-field"
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="status"
                  label={translateText(
                    'administration.healthAndSafety.columns.status'
                  )}
                  name="status"
                  control={control}
                  itemList={statusOptions}
                  errorMessage={errors.status?.message}
                  isRequired
                  data-testid="status-field"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.healthAndSafety.labels.resolutionPrevention'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="resolution"
                  label={translateText(
                    'administration.healthAndSafety.labels.resolution'
                  )}
                  name="resolution"
                  control={control}
                  errorMessage={errors.resolution?.message}
                  multiline
                  rows={3}
                  data-testid="resolution-field"
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="preventiveMeasures"
                  label={translateText(
                    'administration.healthAndSafety.labels.preventiveMeasures'
                  )}
                  name="preventiveMeasures"
                  control={control}
                  errorMessage={errors.preventiveMeasures?.message}
                  multiline
                  rows={3}
                  data-testid="preventive-measures-field"
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditHealthIncidentsView;
